import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, mediaUrl, uploadVideo, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Plus, Sparkles, Trash2, Upload, Check, Clock, X, Eye, Leaf, Fish, Milk, Wheat, Flame, Snowflake, Croissant, Utensils } from "lucide-react";
import RecipePreview from "@/components/RecipePreview";

const AISLE = {
  "Produce": { icon: Leaf, tint: "bg-black/5 text-nerd-light" },
  "Meat & Seafood": { icon: Fish, tint: "bg-coral/15 text-coral" },
  "Dairy": { icon: Milk, tint: "bg-sky/15 text-sky" },
  "Pantry": { icon: Wheat, tint: "bg-sand/15 text-sand" },
  "Spices": { icon: Flame, tint: "bg-nerd-light/15 text-orchid" },
  "Bakery": { icon: Croissant, tint: "bg-sand/15 text-sand" },
  "Frozen": { icon: Snowflake, tint: "bg-sky/15 text-sky" },
  "Other": { icon: Utensils, tint: "bg-black/8 text-nerd-light/70" },
};

const STATUS = {
  draft: { label: "Draft", cls: "bg-black/10 text-nerd-light" },
  pending: { label: "In review", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "Published", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Not approved", cls: "bg-red-100 text-red-600" },
};

const blank = { title: "", description: "", reel_url: "", video_url: "", recipe: null };

export default function CreatorDashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [profile, setProfile] = useState({ name: "", handle: "", bio: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [editing, setEditing] = useState(null); // submission draft object or null
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    if (user) setProfile({ name: user.name || "", handle: user.handle || "", bio: user.bio || "" });
  }, [user]);

  const load = async () => {
    try { const { data } = await api.get("/creator/submissions"); setSubs(data.submissions || []); } catch { /* */ }
  };
  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    try { await api.put("/me/profile", { name: profile.name, handle: profile.handle, bio: profile.bio }); await refresh?.(); toast.success("Profile saved"); }
    catch { toast.error("Could not save profile"); } finally { setSavingProfile(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/creator/submissions/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Could not delete"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-end justify-between mb-8 animate-fade-up">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Your recipes</h1>
        </div>
        <button data-testid="new-recipe-btn" onClick={() => { setEditing({ ...blank }); setShowNew(true); }}
          className="inline-flex items-center gap-2 bg-nerd-light text-white text-sm font-semibold px-5 py-3 rounded-full hover:opacity-90 transition-opacity">
          <Plus className="w-4 h-4" /> New recipe
        </button>
      </div>

      {/* Profile */}
      <div className="bg-nerd-card border border-black/8 rounded-3xl p-6 mb-8 animate-fade-up">
        <h2 className="font-display text-lg font-bold mb-4">Profile</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input data-testid="profile-name" value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
            placeholder="Display name" className="bg-nerd-depth border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-mint" />
          <input data-testid="profile-handle" value={profile.handle} onChange={(e) => setProfile((p) => ({ ...p, handle: e.target.value }))}
            placeholder="@handle" className="bg-nerd-depth border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-mint" />
        </div>
        <textarea data-testid="profile-bio" value={profile.bio} onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))}
          placeholder="Short bio" className="w-full mt-4 min-h-[80px] resize-none bg-nerd-depth border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-mint" />
        <button data-testid="save-profile-btn" onClick={saveProfile} disabled={savingProfile}
          className="mt-4 inline-flex items-center gap-2 border border-black/12 px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-black/5 transition-colors">
          {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save profile
        </button>
      </div>

      {/* Submissions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="creator-submissions">
        {subs.length === 0 && <p className="text-nerd-light/50 text-sm col-span-full">No recipes yet. Click "New recipe" to create your first one.</p>}
        {subs.map((s) => {
          const st = STATUS[s.status] || STATUS.draft;
          return (
            <div key={s.id} data-testid={`sub-${s.id}`} className="bg-nerd-card border border-black/8 rounded-2xl overflow-hidden flex flex-col">
              <div className="relative aspect-video bg-nerd-lift">
                {s.video_url && <video muted loop playsInline autoPlay className="w-full h-full object-cover" src={mediaUrl(s.video_url)} />}
                <span className={`absolute top-2 right-2 text-[11px] font-semibold px-2 py-1 rounded-full ${st.cls}`}>{st.label}</span>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="font-display font-bold leading-tight line-clamp-2">{s.title || "Untitled recipe"}</h3>
                <div className="mt-3 flex gap-2">
                  {s.status !== "approved" && (
                    <button data-testid={`edit-${s.id}`} onClick={() => { setEditing({ ...blank, ...s }); setShowNew(true); }}
                      className="text-xs font-semibold border border-black/12 px-3 py-1.5 rounded-full hover:bg-black/5">Edit</button>
                  )}
                  {s.status === "approved" && s.published_recipe_id && (
                    <button onClick={() => navigate(`/recipe/${s.published_recipe_id}`)} className="text-xs font-semibold border border-black/12 px-3 py-1.5 rounded-full hover:bg-black/5">View live</button>
                  )}
                  {s.status !== "approved" && (
                    <button data-testid={`delete-${s.id}`} onClick={() => remove(s.id)} className="text-xs text-red-500 border border-red-200 px-3 py-1.5 rounded-full hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showNew && <RecipeEditor initial={editing} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function RecipeEditor({ initial, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState(initial);
  const [caption, setCaption] = useState("");
  const [generating, setGenerating] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const recipe = form.recipe;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setR = (k, v) => setForm((f) => ({ ...f, recipe: { ...f.recipe, [k]: v } }));

  const updateIngredient = (idx, key, val) => setForm((f) => {
    const ings = [...(f.recipe.ingredients || [])];
    ings[idx] = { ...ings[idx], [key]: val };
    return { ...f, recipe: { ...f.recipe, ingredients: ings } };
  });
  const addIngredient = () => setForm((f) => ({ ...f, recipe: { ...f.recipe, ingredients: [...(f.recipe.ingredients || []), { name: "", quantity: 0, unit: "", aisle: "Other" }] } }));
  const removeIngredient = (idx) => setForm((f) => ({ ...f, recipe: { ...f.recipe, ingredients: (f.recipe.ingredients || []).filter((_, i) => i !== idx) } }));

  const updateStep = (idx, key, val) => setForm((f) => {
    const steps = [...(f.recipe.steps || [])];
    steps[idx] = { ...steps[idx], [key]: val };
    return { ...f, recipe: { ...f.recipe, steps } };
  });
  const addStep = () => setForm((f) => ({ ...f, recipe: { ...f.recipe, steps: [...(f.recipe.steps || []), { instruction: "", duration_min: 0 }] } }));
  const removeStep = (idx) => setForm((f) => ({ ...f, recipe: { ...f.recipe, steps: (f.recipe.steps || []).filter((_, i) => i !== idx) } }));

  const generate = async () => {
    if (!caption.trim()) { toast.error("Paste your reel caption or notes first"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/creator/generate", { text: caption });
      setForm((f) => ({ ...f, recipe: data.recipe, title: f.title || data.recipe.title || "", description: f.description || data.recipe.description || "" }));
      toast.success("Recipe generated — review & edit below");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const onVideo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video file"); return; }
    setUploadPct(0);
    try { const url = await uploadVideo(file, setUploadPct); set("video_url", url); toast.success("Video uploaded"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploadPct(null); }
  };

  const save = async (status, agreedTerms = false) => {
    const payload = { title: form.title, description: form.description, reel_url: form.reel_url, video_url: form.video_url, recipe, status, agreed_terms: agreedTerms };
    setSaving(true);
    try {
      if (form.id) await api.put(`/creator/submissions/${form.id}`, payload);
      else await api.post("/creator/submissions", payload);
      toast.success(status === "pending" ? "Submitted for review!" : "Draft saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="recipe-editor">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 sm:p-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl font-extrabold">{form.id ? "Edit recipe" : "New recipe"}</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>

        {!recipe ? (
          <div className="space-y-3">
            <label className="text-xs font-mono uppercase tracking-widest text-nerd-light/50">Paste your reel caption / notes</label>
            <textarea data-testid="caption-input" value={caption} onChange={(e) => setCaption(e.target.value)}
              placeholder="e.g. Creamy tuscan pasta — garlic, sun-dried tomatoes, spinach, cream..." className="w-full min-h-[140px] resize-none bg-nerd-depth border border-black/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-mint" />
            <button data-testid="generate-recipe-btn" onClick={generate} disabled={generating}
              className="w-full inline-flex items-center justify-center gap-2 bg-nerd-light text-white font-semibold px-5 py-3 rounded-xl hover:opacity-90 disabled:opacity-60">
              {generating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><Sparkles className="w-4 h-4" /> Generate recipe with AI</>}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <Field label="Title"><input data-testid="edit-title" value={form.title} onChange={(e) => { set("title", e.target.value); setR("title", e.target.value); }} className="fi-input" /></Field>
            <Field label="Description"><textarea data-testid="edit-desc" value={recipe.description || ""} onChange={(e) => { setR("description", e.target.value); set("description", e.target.value); }} className="fi-input min-h-[70px] resize-none" /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Time (min)"><input type="number" value={recipe.total_time_min || 0} onChange={(e) => setR("total_time_min", Number(e.target.value))} className="fi-input" /></Field>
              <Field label="Servings"><input type="number" value={recipe.servings || 0} onChange={(e) => setR("servings", Number(e.target.value))} className="fi-input" /></Field>
              <Field label="Difficulty"><input value={recipe.difficulty || ""} onChange={(e) => setR("difficulty", e.target.value)} className="fi-input" /></Field>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-mono uppercase tracking-widest text-nerd-light/50">Ingredients</label>
                <button type="button" data-testid="add-ingredient-btn" onClick={addIngredient}
                  className="inline-flex items-center gap-1 text-xs font-semibold border border-black/12 px-3 py-1 rounded-full hover:bg-black/5"><Plus className="w-3 h-3" /> Add</button>
              </div>
              <div className="space-y-2" data-testid="ingredient-cards">
                {(recipe.ingredients || []).length === 0 && <p className="text-xs text-nerd-light/40">No ingredients yet. Click "Add" to create one.</p>}
                {(recipe.ingredients || []).map((ing, i) => {
                  const meta = AISLE[ing.aisle] || AISLE.Other;
                  const Icon = meta.icon;
                  return (
                    <div key={i} data-testid={`ingredient-card-${i}`} className="flex items-center gap-2 bg-nerd-depth border border-black/10 rounded-xl p-2">
                      <label className={`relative shrink-0 w-11 h-11 rounded-xl flex items-center justify-center cursor-pointer ${meta.tint}`} title="Category">
                        <Icon className="w-5 h-5" />
                        <select data-testid={`ingredient-aisle-${i}`} value={ing.aisle || "Other"} onChange={(e) => updateIngredient(i, "aisle", e.target.value)}
                          className="absolute inset-0 opacity-0 cursor-pointer">
                          {Object.keys(AISLE).map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                      </label>
                      <input data-testid={`ingredient-name-${i}`} value={ing.name} onChange={(e) => updateIngredient(i, "name", e.target.value)}
                        placeholder="Ingredient" className="flex-1 min-w-0 bg-white border border-black/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-mint" />
                      <input data-testid={`ingredient-qty-${i}`} type="number" value={ing.quantity || 0} onChange={(e) => updateIngredient(i, "quantity", Number(e.target.value))}
                        className="w-16 bg-white border border-black/10 rounded-lg px-2 py-2 text-sm text-center outline-none focus:border-mint" />
                      <input data-testid={`ingredient-unit-${i}`} value={ing.unit || ""} onChange={(e) => updateIngredient(i, "unit", e.target.value)}
                        placeholder="unit" className="w-16 bg-white border border-black/10 rounded-lg px-2 py-2 text-sm outline-none focus:border-mint" />
                      <button type="button" data-testid={`remove-ingredient-${i}`} onClick={() => removeIngredient(i)}
                        className="shrink-0 w-8 h-8 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-mono uppercase tracking-widest text-nerd-light/50">Steps</label>
                <button type="button" data-testid="add-step-btn" onClick={addStep}
                  className="inline-flex items-center gap-1 text-xs font-semibold border border-black/12 px-3 py-1 rounded-full hover:bg-black/5"><Plus className="w-3 h-3" /> Add</button>
              </div>
              <div className="space-y-2" data-testid="step-cards">
                {(recipe.steps || []).length === 0 && <p className="text-xs text-nerd-light/40">No steps yet. Click "Add" to create one.</p>}
                {(recipe.steps || []).map((s, i) => (
                  <div key={i} data-testid={`step-card-${i}`} className="flex gap-2 bg-nerd-depth border border-black/10 rounded-xl p-2">
                    <div className="shrink-0 w-11 h-11 rounded-xl bg-black/5 text-nerd-light flex items-center justify-center font-mono text-sm font-semibold">{String(i + 1).padStart(2, "0")}</div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <textarea data-testid={`step-instruction-${i}`} value={s.instruction || ""} onChange={(e) => updateStep(i, "instruction", e.target.value)}
                        placeholder="Describe this step" className="w-full min-h-[56px] resize-none bg-white border border-black/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-mint" />
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-nerd-light/40" />
                        <input data-testid={`step-duration-${i}`} type="number" value={s.duration_min || 0} onChange={(e) => updateStep(i, "duration_min", Number(e.target.value))}
                          className="w-16 bg-white border border-black/10 rounded-lg px-2 py-1 text-xs text-center outline-none focus:border-mint" />
                        <span className="text-xs text-nerd-light/40">mins</span>
                      </div>
                    </div>
                    <button type="button" data-testid={`remove-step-${i}`} onClick={() => removeStep(i)}
                      className="shrink-0 w-8 h-8 rounded-full text-red-500 hover:bg-red-50 flex items-center justify-center self-start"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <Field label="Tags (comma separated)">
              <input value={(recipe.tags || []).join(", ")} onChange={(e) => setR("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} className="fi-input" />
            </Field>

            <Field label="Reel video (required)">
              {form.video_url ? (
                <div className="flex items-center gap-3">
                  <video muted loop playsInline autoPlay className="w-24 h-32 object-cover rounded-lg" src={mediaUrl(form.video_url)} />
                  <button onClick={() => set("video_url", "")} className="text-xs text-red-500 underline">Remove</button>
                </div>
              ) : (
                <label data-testid="video-upload" className="flex items-center gap-2 border border-dashed border-black/20 rounded-xl px-4 py-3 text-sm cursor-pointer hover:bg-black/5">
                  {uploadPct !== null ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading {uploadPct}%</> : <><Upload className="w-4 h-4" /> Upload reel video (mp4, max 100MB)</>}
                  <input type="file" accept="video/*" className="hidden" onChange={(e) => onVideo(e.target.files?.[0])} />
                </label>
              )}
            </Field>
            <Field label="Reel link (optional)"><input value={form.reel_url} onChange={(e) => set("reel_url", e.target.value)} placeholder="https://www.instagram.com/reel/…" className="fi-input" /></Field>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button type="button" data-testid="preview-recipe-btn" onClick={() => setShowPreview(true)}
                className="inline-flex items-center justify-center gap-2 border border-black/12 px-5 py-3 rounded-xl text-sm font-semibold hover:bg-black/5">
                <Eye className="w-4 h-4" /> Preview
              </button>
              <div className="flex gap-3 flex-1">
                <button data-testid="save-draft-btn" onClick={() => save("draft")} disabled={saving}
                  className="flex-1 border border-black/12 px-5 py-3 rounded-xl text-sm font-semibold hover:bg-black/5 disabled:opacity-60">Save draft</button>
                <button data-testid="submit-review-btn" onClick={() => { setAgreed(false); setShowTerms(true); }} disabled={saving}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-nerd-light text-white px-5 py-3 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />} Submit for review
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showPreview && (
        <div data-testid="creator-preview-overlay" className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto" onClick={() => setShowPreview(false)}>
          <div className="bg-white w-full max-w-6xl rounded-none sm:rounded-3xl min-h-full sm:min-h-0 my-0 sm:my-6" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 sm:px-8 py-4 bg-white/90 backdrop-blur border-b border-black/8 rounded-t-3xl">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-nerd-light/40">Preview · as users will see it</p>
              <button data-testid="close-creator-preview-btn" onClick={() => setShowPreview(false)}
                className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-4 sm:px-8 py-6 sm:py-8">
              <RecipePreview recipe={recipe} videoUrl={form.video_url} title={form.title} description={form.description} />
            </div>
          </div>
        </div>
      )}
      {showTerms && (
        <div data-testid="creator-terms-overlay" className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 sm:px-8 pt-6 pb-4 border-b border-black/8">
              <h2 className="font-display text-xl sm:text-2xl font-extrabold pr-4">Creator Content License & Consent Agreement</h2>
              <button data-testid="close-terms-btn" onClick={() => setShowTerms(false)} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center shrink-0"><X className="w-5 h-5" /></button>
            </div>
            <div data-testid="terms-body" className="overflow-y-auto px-6 sm:px-8 py-5">
              <CreatorTerms creatorName={user?.name || ""} creatorHandle={user?.handle || ""} />
            </div>
            <div className="border-t border-black/8 px-6 sm:px-8 py-5 space-y-4">
              <label data-testid="agree-terms-label" className="flex items-start gap-3 cursor-pointer select-none">
                <input type="checkbox" data-testid="agree-terms-checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 w-5 h-5 rounded border-black/25 accent-black shrink-0" />
                <span className="text-sm text-nerd-light leading-relaxed">I own or have rights to this Content, and I agree to the terms above.</span>
              </label>
              <div className="flex gap-3">
                <button data-testid="cancel-terms-btn" onClick={() => setShowTerms(false)}
                  className="flex-1 border border-black/12 px-5 py-3 rounded-xl text-sm font-semibold hover:bg-black/5">Cancel</button>
                <button data-testid="agree-submit-btn" disabled={!agreed || saving} onClick={() => { setShowTerms(false); save("pending", true); }}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-nerd-light text-white px-5 py-3 rounded-xl text-sm font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Agree & submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-widest text-nerd-light/50 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function TermsSection({ n, title, children }) {
  return (
    <div className="mb-4">
      <h3 className="font-display font-bold text-nerd-light mb-1.5">{n}. {title}</h3>
      <div className="text-sm text-nerd-light/70 leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

function CreatorTerms({ creatorName = "", creatorHandle = "" }) {
  const today = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const nameLine = [creatorName, creatorHandle].filter(Boolean).join(" · ") || "—";
  return (
    <div>
      <p className="text-sm text-nerd-light/70 leading-relaxed mb-5">
        This agreement applies when you submit a recipe, video, or related content ("Content") to Flavour Intelligence
        through the Creator Dashboard.
      </p>

      <TermsSection n={1} title="Content Ownership">
        <p>You confirm that:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>You are the original creator and owner of the Content you're submitting, or you hold all necessary rights and permissions to submit it.</li>
          <li>The Content does not infringe on any third party's copyright, trademark, privacy, or other rights.</li>
          <li>You have the authority to grant the license described below.</li>
        </ul>
      </TermsSection>

      <TermsSection n={2} title="License Grant to Flavour Intelligence">
        <p>By submitting Content, you grant Flavour Intelligence a non-exclusive, worldwide license (compensated as described in Section 4) to:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Host, display, and distribute your Content within the Flavour Intelligence app</li>
          <li>Use Claude (Anthropic's AI) to analyze your Content and generate structured, personalized recipes derived from it (e.g., ingredient substitutions, dietary adaptations, step-by-step cooking instructions)</li>
          <li>Feature your Content and derived recipes in the app's discovery, search, and recommendation surfaces</li>
          <li>Use anonymized or aggregated data from your Content for product improvement</li>
        </ul>
        <p>You retain full ownership of your original Content. This license does not transfer copyright to Flavour Intelligence.</p>
      </TermsSection>

      <TermsSection n={3} title="Attribution">
        <p>Flavour Intelligence will:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Credit you as the original creator on all Content and AI-generated recipes derived from it</li>
          <li>Display your creator profile and handle alongside your Content</li>
          <li>Never present your Content as created by Flavour Intelligence or another party</li>
        </ul>
      </TermsSection>

      <TermsSection n={4} title="Compensation">
        <p>Flavour Intelligence pays a flat fee of €8.99 per recipe that is reviewed, approved, and featured in the app.</p>
        <p>Payment is made only for Content that is approved and published — submission alone does not guarantee payment (see Section 5).</p>
        <p>Payment is a one-time fee per featured recipe. It does not represent an ongoing royalty or usage-based payment for that recipe.</p>
        <p>Payment will be made via PayPal, sent to the email address associated with your registered Creator Account, within 15 business days of the recipe being featured. This must be the same email address as your PayPal account, or payment cannot be completed.</p>
      </TermsSection>

      <TermsSection n={5} title="Review & Approval">
        <p>Submitted Content is reviewed by Flavour Intelligence before publication. We reserve the right to decline Content that doesn't meet quality, safety, or content guidelines, or to request revisions.</p>
        <p>Approval timelines and criteria may be communicated separately in creator guidelines.</p>
      </TermsSection>

      <TermsSection n={6} title="Revocation">
        <p>You may request removal of your Content from Flavour Intelligence at any time by contacting creatorsupport@flavourintelligence.app.</p>
        <p>We will remove the Content and any associated AI-generated recipes from active display within 30 business days.</p>
        <p>Removal does not retroactively affect users who already saved or interacted with the Content before removal, but it will no longer be featured or served going forward.</p>
      </TermsSection>

      <TermsSection n={7} title="No Exclusivity">
        <p>This agreement is non-exclusive. You are free to post, license, or otherwise use your Content anywhere else, including Instagram or other platforms.</p>
      </TermsSection>

      <TermsSection n={8} title="Changes to This Agreement">
        <p>If we materially change these terms, we'll notify creators and, where required, request renewed consent before continuing to use previously submitted Content under the new terms.</p>
      </TermsSection>

      <p className="text-sm text-nerd-light/70 leading-relaxed mt-5 pt-4 border-t border-black/8">
        By submitting Content through the Flavour Intelligence Creator Dashboard, you confirm that you have read,
        understood, and agree to this License &amp; Consent Agreement.
      </p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div className="bg-nerd-depth border border-black/10 rounded-xl px-4 py-3">
          <p className="text-xs font-mono uppercase tracking-widest text-nerd-light/40 mb-1">Creator Name / Handle</p>
          <p className="font-medium text-nerd-light" data-testid="terms-creator-name">{nameLine}</p>
        </div>
        <div className="bg-nerd-depth border border-black/10 rounded-xl px-4 py-3">
          <p className="text-xs font-mono uppercase tracking-widest text-nerd-light/40 mb-1">Date</p>
          <p className="font-medium text-nerd-light" data-testid="terms-date">{today}</p>
        </div>
      </div>
    </div>
  );
}
